import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { ScopeMode } from 'types/channel';
import { subscribeSphereToChannel, recordRecentSphere } from '../../../api/marketplaceApi';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import type { ContextOption } from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import ScopeSegmentAtom from '../../atoms/ScopeSegment/ScopeSegmentAtom';
import HelpTipAtom from '../../atoms/HelpTip/HelpTipAtom';
import SphereSelectDialogOrganism from './SphereSelectDialogOrganism';
import './SpherePickerDialogOrganism.scss';

export interface SpherePickerDialogOrganismProps {
  open: boolean;
  channelId: string;
  subscribedSphereIds?: string[];
  /** Initial cascade scope ("direct" or "recursive") applied when the dialog opens. */
  initialScopeMode?: ScopeMode;
  /** Sphere ID to use as the anchor for the Nearby tab. When absent, Nearby tab is disabled. */
  nearbyAnchorSphereId?: string;
  onClose: () => void;
  onSubscribed: () => void;
  /** Contexts made available in the context selector. */
  availableContexts?: ContextOption[];
  /**
   * System name of the context selected by default. Defaults to 'center_hierarchy_context'.
   * The context's level0PropertyFilters scope the directory (prevents period-sphere flood).
   */
  defaultContextSystemName?: string;
}

const SpherePickerDialogOrganism: React.FC<SpherePickerDialogOrganismProps> = ({
  open,
  channelId,
  subscribedSphereIds = [],
  initialScopeMode = 'recursive',
  nearbyAnchorSphereId,
  onClose,
  onSubscribed,
  availableContexts,
  defaultContextSystemName,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [scopeMode, setScopeMode] = useState<ScopeMode>(initialScopeMode);
  const [error, setError] = useState<string | null>(null);

  // Reset wrapper state on each dialog open.
  useEffect(() => {
    if (open) {
      setScopeMode(initialScopeMode);
      setError(null);
    }
  }, [open, initialScopeMode]);

  const handleSelect = useCallback(
    async (sphere: SphereSearchResult): Promise<boolean> => {
      if (!apiClients?.schemaClient || !organizationId) return false;
      setError(null);
      try {
        await subscribeSphereToChannel(
          apiClients.schemaClient,
          channelId,
          sphere.id,
          scopeMode,
        );
        if (apiClients.searchClient) {
          recordRecentSphere(apiClients.searchClient, sphere.id).catch(() => undefined);
        }
        onSubscribed();
        return true;
      } catch (err: unknown) {
        if (err instanceof Error && err.message.includes('409')) {
          setError(t('sphereChannels.subscribeSphere.errorDuplicate'));
        } else {
          setError(t('sphereChannels.subscribeSphere.error'));
        }
        return false;
      }
    },
    [apiClients, channelId, scopeMode, organizationId, onSubscribed, t]
  );

  const scopeBar = (
    <div className="sphere-picker-dialog__scope-bar">
      <span className="sphere-picker-dialog__scope-label">
        {t('sphereChannels.subscriptionsPanel.scopeSegment.applyLabel')}
      </span>
      <HelpTipAtom text={t('sphereChannels.subscriptionsPanel.scopeSegment.applyHelpTip')} />
      <span className="sphere-picker-dialog__scope-spacer" />
      <ScopeSegmentAtom value={scopeMode} onChange={setScopeMode} size="small" />
    </div>
  );

  const errorNode = error ? (
    <div className="sphere-picker-dialog__error" role="alert">
      {error}
    </div>
  ) : null;

  return (
    <SphereSelectDialogOrganism
      open={open}
      onClose={onClose}
      titleKey="sphereChannels.subscribeSphere.title"
      onSelect={handleSelect}
      excludedSphereIds={subscribedSphereIds}
      nearbyAnchorSphereId={nearbyAnchorSphereId}
      availableContexts={availableContexts}
      defaultContextSystemName={defaultContextSystemName}
      headerExtras={scopeBar}
      errorNode={errorNode}
      permission="edit"
    />
  );
};

export default SpherePickerDialogOrganism;
